import { Button, Col, Row, Space, theme, FormInstance } from "antd";
import { useMemo } from "react";
import Data from "./Data";
import DynamicForm from "./DynamicForm";
import { DataAction, DataColumn, DataRequest, DataResult } from "./Data.default";
import { DynamicSchema } from "./DynamicForm.default";
import { SearchQuery } from "../code/search-query/search-query";
import { useManagedGrid } from "../hooks/useManagedGrid";

/**
 * The shared shell for searchable list-grid wrappers: a bordered search form (DynamicForm)
 * with Clear/Search buttons next to a virtual Data grid. Each module supplies only what
 * legitimately varies — columns, search schema, query mapping, request fn, row actions —
 * while the duplicated state/JSX lives here (via useManagedGrid). Extends the existing
 * Data / DynamicForm / createGridRequest / buildSearchQuery conventions; it does not
 * replace them. A grid needing custom layout can use useManagedGrid directly instead.
 */
export interface ManagedGridViewProps {
  readonly trigger?: string;
  /**
   * The search-form schema. Pass a **factory** `(form) => schema` when filter requests need to read sibling
   * field values (cascading / scoped filters) — it receives the grid's own form instance.
   */
  readonly schema:
    | DynamicSchema
    | ((form: FormInstance<any>) => DynamicSchema);
  readonly columns: DataColumn[];
  readonly mapQuery: (values: any) => SearchQuery;
  readonly request: (params: DataRequest) => Promise<DataResult>;
  readonly actions?: ((record: any) => DataAction[]) | DataAction[];
  readonly pageSize?: number;
  readonly rowKey?: (item: any) => any;
  readonly onSearch?: (query: SearchQuery) => void;
  readonly clearResetsValid?: boolean;
  readonly formClassName?: string;
  /** Pre-populate the search form on mount. */
  readonly initialValues?: Record<string, any>;
  /** When true (with `initialValues`), run an initial search on mount so the grid loads immediately. */
  readonly autoSearch?: boolean;
}

function ManagedGridView(props: ManagedGridViewProps) {
  const { token } = theme.useToken();
  const grid = useManagedGrid({
    trigger: props.trigger,
    mapQuery: props.mapQuery,
    onSearch: props.onSearch,
    clearResetsValid: props.clearResetsValid,
    initialValues: props.initialValues,
    autoSearch: props.autoSearch,
  });

  // Resolve a schema factory against the grid's own form so scoped filter requests can read sibling values.
  const schema = useMemo(
    () =>
      typeof props.schema === "function" ? props.schema(grid.form) : props.schema,
    [props.schema, grid.form],
  );

  return (
    <Row className="flex flex-auto flex-row flex-nowrap overflow-hidden">
      {/* Search form */}
      <Col
        className="mr-4 flex flex-col border border-solid pl-2 pr-2"
        style={{
          borderColor: token.colorBorder,
          borderRadius: token.borderRadius,
        }}
      >
        <Space className="flex flex-row justify-end pb-2 pt-2">
          <Button
            disabled={!grid.valuesValid || grid.loading}
            onClick={grid.onClearData}
          >
            Clear
          </Button>
          <Button
            type="primary"
            loading={grid.loading}
            disabled={!grid.valuesValid}
            onClick={grid.onSearchData}
          >
            Search
          </Button>
        </Space>
        <DynamicForm
          className={props.formClassName ?? "w-72 overflow-y-auto"}
          form={grid.form}
          schema={schema}
        />
      </Col>
      {/* Grid */}
      <Data
        columns={props.columns}
        context={grid.context}
        pageSize={props.pageSize ?? 25}
        request={props.request}
        rowKey={props.rowKey ?? (({ uid }) => uid)}
        onLoading={grid.onLoading}
        actions={props.actions}
      />
    </Row>
  );
}

export default ManagedGridView;
